import {
  IUserReportAddResult,
  IUserReportAddResultTemplate,
  TStiReportData,
} from '@infotech-soe/merps/types/Reports';
import { IStreamDeclaration } from '@infotech-soe/merps/types/streams';
import {
  ActionName,
  CategoryName,
  DbId,
  DisplayModeName,
  IContext,
  TDatasetName,
  TRecordViewName,
} from '@infotech-soe/metaschema';

import AsyncLRUCache from '../../framework/caching/AsyncLRUCache';
import { RemoteEvent } from '../../infrastructure/network/RemoteEvent';
import { LocalStorageObjectStorage } from '../../infrastructure/persistence/LocalStorageObjectStorage';
import IApiProvider, {
  ReportTemplate,
  TActionArgsValues,
} from '../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../infrastructure/providers/pb-adapter/MerpsApiProvider';

import { AbstractPrintService } from './AbstractPrintService';

type ReportName = string;
type ReportChecksum = string;

class PrintServiceSingleton implements AbstractPrintService {
  private readonly api: IApiProvider;
  private readonly checksumCache: AsyncLRUCache<ReportName, ReportChecksum>;
  private readonly storage: LocalStorageObjectStorage;

  constructor() {
    this.api = new MerpsApiProvider();
    this.checksumCache = new AsyncLRUCache(20, this._getTemplate);
    this.storage = new LocalStorageObjectStorage('repTemplate', 32);
    this.listenForRemoteEvents();
  }

  public getExportedReport(
    action: ActionName,
    context: IContext,
    args: TActionArgsValues
  ): Promise<IUserReportAddResult> {
    return this.api.rpc.callMethod('print', 'getExportedReport', [
      action,
      context,
      args,
    ]);
  }

  public getPrintStream(documentId: string): Promise<IStreamDeclaration> {
    return this.api.rpc.callMethod('print', 'getReportPdf', [documentId]);
  }

  public getReportData(
    action: ActionName,
    context: IContext,
    args: TActionArgsValues
  ): Promise<TStiReportData> {
    return this.api.rpc.callMethod('print', 'getReportData', [
      action,
      context,
      args,
    ]);
  }

  public getReportDataFromLibraryRoutine(
    categoryName: CategoryName,
    displayModeName: DisplayModeName,
    datasetName: TDatasetName,
    recordViewName: TRecordViewName,
    id: DbId
  ): Promise<TStiReportData> {
    return this.api.rpc.callMethod('print', 'getReportDataFromLibraryRoutine', [
      categoryName,
      displayModeName,
      datasetName,
      recordViewName,
      id,
    ]);
  }

  public async getTemplate(name: string): Promise<ReportTemplate> {
    const checksum = await this.checksumCache.getEntry(name);
    let template = await this.storage.get(checksum);

    if (template) {
      return template;
    }
    const templateResult: IUserReportAddResultTemplate =
      await this.api.rpc.callMethod('print', 'getTemplate', [name]);

    const templateBuffer = await this.api.streams.read(templateResult.id);
    template = Buffer.from(templateBuffer).toString();

    if (template) {
      await this.storage.set(checksum, template);
      return template;
    }

    throw new Error('Template not found');
  }

  public getTemplateChecksum(name: string): Promise<string> {
    return this.api.rpc.callMethod('print', 'getTemplateChecksum', [name]);
  }

  private readonly _getTemplate = async (name: string): Promise<string> => {
    const checksum = await this.getTemplateChecksum(name);
    return checksum;
  };

  private listenForRemoteEvents(): void {
    this.api.rpc.onRemoteEvent.addListener(
      RemoteEvent.listenerFor('schemas', 'reports-updated', () => {
        this.checksumCache.clearEntries();
      })
    );
  }

  public getReportDataFromPrintAction(
    action: ActionName,
    context: IContext,
    args: TActionArgsValues
  ): Promise<TStiReportData> {
    return this.api.print.getReportDataFromPrintAction(action, context, args);
  }
}

const PrintService = (() => {
  let instance: PrintServiceSingleton;

  return new Proxy(PrintServiceSingleton, {
    construct(Target: typeof PrintServiceSingleton): AbstractPrintService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default PrintService;
